import { classNameStem } from './classNameStem';

// Read-only aggregation for the Missing Levels insights panel. Groups the rows ALREADY LOADED for the
// current view by their deterministic stem (schedule-variant grouping), so recurring rule candidates
// surface above names seen once here. Pure -- no fetch, no mutation. Counts are within the current view
// (the loaded tracking year / filters), which is exactly the "current/recent" signal an auditor wants.

const SEASON_LABEL = { 1: 'Spring', 2: 'Summer', 3: 'Fall', 4: 'Winter' };

function seasonText(row) {
  const s = SEASON_LABEL[row.trackingseason];
  const y = row.trackingyear;
  if (!y) return null;
  return s ? `${s} ${y}` : String(y);
}

// Returns groups sorted by impact (occurrences desc, then franchise breadth, then stem for stability).
export function groupMissingByStem(rows) {
  const map = new Map();
  for (const row of rows || []) {
    const name = row.class_name ?? '';
    const stem = classNameStem(name) || name;
    if (!map.has(stem)) {
      map.set(stem, {
        stem, occurrences: 0,
        franchises: new Set(), facilities: new Set(), seasons: new Set(),
        names: new Set(), programTypes: new Set(),
      });
    }
    const g = map.get(stem);
    g.occurrences += 1;
    if (row.com_code) g.franchises.add(row.com_code);
    if (row.facility_name) g.facilities.add(row.facility_name);
    const s = seasonText(row);
    if (s) g.seasons.add(s);
    if (name) g.names.add(name);
    if (row.class_program_type) g.programTypes.add(row.class_program_type);
  }

  const groups = Array.from(map.values()).map((g) => ({
    stem: g.stem,
    occurrences: g.occurrences,
    franchiseCount: g.franchises.size,
    facilityCount: g.facilities.size,
    variantCount: g.names.size,
    // com_codes in this group -- lets the rule-authoring dialog set an evidence-aware scope default
    // (a single-franchise observation defaults to that franchise, not global).
    representativeFranchises: Array.from(g.franchises).slice(0, 3),
    representativeFacilities: Array.from(g.facilities).slice(0, 3),
    representativeSeasons: Array.from(g.seasons).slice(0, 3),
    sampleNames: Array.from(g.names).slice(0, 3),
    // The member name a rule seed is derived from. LEXICOGRAPHICALLY SMALLEST, deliberately not
    // sampleNames[0]: that follows row load order (the server's ORDER BY plus the active filters),
    // so the same candidate could seed a different pattern depending on which rows happened to load.
    // Measured 2026-08-11 over the 450 panel-listed TY2026 candidates: members of one stem group
    // disagree on classNamePattern in only 4 groups (0.9%) -- whitespace variants such as
    // "Skills 5-6 (5-6 yrs)  LVL 1" vs a single space -- and load order flips the seed in exactly
    // those 4. Coverage is identical for load-order, lexical and prefer-truncated selection (446 of
    // 450 seeds cover every name in their group), so stability costs nothing. NOTE this is a
    // display/authoring aid only; it does NOT participate in grouping.
    representativeName: Array.from(g.names).sort()[0] || null,
    programType: g.programTypes.size === 1 ? Array.from(g.programTypes)[0]
      : g.programTypes.size > 1 ? 'Mixed' : null,
  }));

  groups.sort((a, b) =>
    b.occurrences - a.occurrences ||
    b.franchiseCount - a.franchiseCount ||
    a.stem.localeCompare(b.stem));

  return groups;
}

// Header stats for the panel. Recurring = a stem seen 2+ times in the current view.
// `singletonGroups` counts names seen ONCE HERE. It was called `oneOffGroups`, which invited the
// panel to assert that such a name is a one-off -- a claim a capped, filtered, single-tracking-year
// view cannot support. The count is unchanged; only the name is, so the wording cannot drift back.
export function summarizeGroups(groups) {
  const recurring = groups.filter((g) => g.occurrences >= 2);
  return {
    totalGroups: groups.length,
    recurringGroups: recurring.length,
    singletonGroups: groups.length - recurring.length,
    totalOfferings: groups.reduce((n, g) => n + g.occurrences, 0),
  };
}
